import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import V2Nav from '../../components/homepage-v2/V2Nav';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { COMPANY, companyRegistrationLine } from '../../constants/company';
import '../../components/homepage-v2/homepageV2.css';

export default function Terms() {
  usePageMeta(
    'Terms and Conditions | Smile Consent',
    'Terms and conditions for Smile Consent dental consent software. Read our service terms, subscriptions, data handling, and practice responsibilities.',
    'https://smileconsent.co.uk/terms'
  );

  return (
    <div className="hpv2 min-h-screen">
      <V2Nav />
      <div className="py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
            <p className="text-gray-600">Last updated: 10 May 2026</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                These Terms and Conditions (&quot;Terms&quot;) govern your use of SmileConsent (&quot;Service&quot;), a digital consent management platform operated by {COMPANY.legalName} (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By accessing or using our Service, you agree to be bound by these Terms. {companyRegistrationLine()}
              </p>
              <p className="text-gray-700">
                The Service is designed to help dental practices document and manage consent workflows. You remain responsible for meeting all applicable laws and professional standards (including GDC and CQC expectations where they apply to your practice). The Service does not guarantee regulatory or inspection outcomes.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>"Service"</strong> means the SmileConsent digital platform and all related software, applications, and services.</li>
                <li><strong>"User"</strong> means any individual or entity that accesses or uses the Service.</li>
                <li><strong>"Practice"</strong> means a dental practice, clinic, or healthcare provider using the Service.</li>
                <li><strong>"Patient Data"</strong> means any personal or medical information entered into the Service.</li>
                <li><strong>"Consent Forms"</strong> means digital consent documents created, stored, or managed through the Service.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Service Description</h2>
              <p className="text-gray-700 mb-4">
                SmileConsent provides a digital platform for dental practices to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Create and manage digital consent forms</li>
                <li>Store and retrieve patient consent records</li>
                <li>Produce records and activity logs to support your own audit and governance processes</li>
                <li>Use templates and AI-assisted drafting tools (where available), which you must review before use</li>
                <li>Manage patient communications and documentation</li>
              </ul>
              <p className="text-gray-700 mb-4">
                The Service is intended to support, but not replace, professional clinical judgment and legal advice.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3a. AI-assisted features</h2>
              <p className="text-gray-700 mb-4">
                Where the Service offers AI-assisted drafting of consent wording, that output is a <strong>draft for clinician review only</strong>. It is not autonomous clinical advice, diagnosis, or a substitute for your professional assessment of the individual patient.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You must review, edit as appropriate, and approve all consent documentation before it is relied upon or sent to patients.</li>
                <li>You are solely responsible for the accuracy, suitability, and completeness of consent materials you issue.</li>
                <li>We may update how AI-assisted features work; you should verify outputs after each material change.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Legal and Regulatory Compliance</h3>
              <p className="text-gray-700 mb-4">
                Users are responsible for ensuring that their use of the Service complies with all applicable laws, regulations, and professional standards, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>General Data Protection Regulation (GDPR)</li>
                <li>Data Protection Act 2018</li>
                <li>GDC Standards for the Dental Team</li>
                <li>CQC Regulations</li>
                <li>Mental Capacity Act 2005</li>
                <li>Any other relevant UK healthcare regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Professional Responsibility</h3>
              <p className="text-gray-700 mb-4">
                Users acknowledge that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>The Service does not constitute legal, medical, or regulatory advice</li>
                <li>Users remain solely responsible for clinical decisions and patient care</li>
                <li>Users must exercise independent professional judgment</li>
                <li>The Service is a tool to support, not replace, professional expertise</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Data Accuracy and Integrity</h3>
              <p className="text-gray-700 mb-4">
                Users are responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Ensuring the accuracy and completeness of all data entered</li>
                <li>Maintaining appropriate backup systems for critical information</li>
                <li>Verifying that consent forms meet their specific legal requirements</li>
                <li>Obtaining proper patient consent before using the Service</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Limitation of Liability</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Service Limitations</h3>
              <p className="text-gray-700 mb-4">
                The Company provides the Service "as is" and "as available" without warranties of any kind. Users acknowledge that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>The Service may not be error-free or uninterrupted</li>
                <li>Technical issues may result in temporary unavailability</li>
                <li>Updates and maintenance may affect Service availability</li>
                <li>Internet connectivity issues may impact Service performance</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Exclusion of Damages</h3>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, the Company shall not be liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Any direct, indirect, incidental, or consequential damages</li>
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>Loss of data or information</li>
                <li>Claims arising from clinical decisions or patient care</li>
                <li>Regulatory non-compliance or legal actions</li>
                <li>Any damages exceeding the amount paid for the Service in the 12 months preceding the claim</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.3 Indemnification</h3>
              <p className="text-gray-700">
                Users agree to indemnify and hold harmless the Company from any claims, damages, or expenses arising from their use of the Service, including but not limited to clinical decisions, regulatory non-compliance, or data breaches caused by user actions.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Protection and Privacy</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Data Processing</h3>
              <p className="text-gray-700 mb-4">
                The Company acts as a data processor for patient data entered by users. Users remain the data controller and are responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Ensuring lawful basis for data processing</li>
                <li>Obtaining appropriate patient consent</li>
                <li>Implementing appropriate security measures</li>
                <li>Responding to patient data subject requests</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Security Measures</h3>
              <p className="text-gray-700 mb-4">
                The Company implements appropriate technical and organizational security measures, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Data encryption in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers and backup systems</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Data Breach Notification</h3>
              <p className="text-gray-700">
                In the event of a data breach affecting patient data, the Company will notify users within 72 hours of becoming aware of the breach, in accordance with GDPR requirements.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The Service and all associated software, content, and materials are owned by the Company and protected by intellectual property laws. Users are granted a limited, non-exclusive license to use the Service for their intended purpose.
              </p>
              <p className="text-gray-700">
                Users retain ownership of their patient data and consent forms, but grant the Company a license to process and store this data to provide the Service.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Service Availability and Support</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 Uptime Commitment</h3>
              <p className="text-gray-700 mb-4">
                The Company aims to maintain 99.5% Service availability, excluding scheduled maintenance windows. However, this is not guaranteed and may vary due to factors beyond our control.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">8.2 Support Services</h3>
              <p className="text-gray-700 mb-4">
                Support is available during business hours (Monday-Friday, 9:00 AM - 5:00 PM GMT). Emergency support may be available for critical issues affecting patient care.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">8.3 Updates and Changes</h3>
              <p className="text-gray-700">
                The Company may update the Service to improve functionality, security, or compliance. Users will be notified of significant changes in advance.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination and Suspension</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 User Termination</h3>
              <p className="text-gray-700 mb-4">
                Users may terminate their account at any time by providing 30 days written notice. Upon termination, users will have access to export their data for 30 days.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">9.2 Company Termination</h3>
              <p className="text-gray-700 mb-4">
                The Company may suspend or terminate access to the Service if:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Users violate these Terms</li>
                <li>Users engage in fraudulent or illegal activities</li>
                <li>Payment obligations are not met</li>
                <li>Regulatory requirements necessitate such action</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">9.3 Data Retention</h3>
              <p className="text-gray-700">
                Following termination, the Company will retain data for the minimum period required by law or regulation, after which it will be securely deleted.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law and Disputes</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
              <p className="text-gray-700">
                The Company encourages users to contact us directly to resolve any issues before pursuing legal action.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                The Company may update these Terms from time to time. Users will be notified of material changes via email or through the Service. Continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms or the Service, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Company:</strong> {COMPANY.legalName}</p>
                <p className="text-gray-700"><strong>Company number:</strong> {COMPANY.number}</p>
                <p className="text-gray-700"><strong>Email:</strong> info@smileconsent.co.uk</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Legal Disclaimer</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-yellow-800 text-sm">
                  <strong>Important:</strong> These Terms and Conditions are designed to protect both the Company and users. However, they do not constitute legal advice. Users should consult with their own legal counsel to ensure compliance with applicable laws and regulations in their jurisdiction. The Company strongly recommends that users seek independent legal advice regarding their specific use case and regulatory requirements.
                </p>
              </div>
            </div>

            <div className="text-center mt-12 mb-8">
              <CompanyDisclosure tone="light" />
            </div>
            <div className="text-center mt-12">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
} 